import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import HelpIcon from 'src/components/help-icon';
import { ProfileImageRounded } from 'src/components/ProfileImageRounded';
import { useNotifications } from 'src/notifications';
import { isStandardErrorResponse } from 'src/api/common';
import {
    type IContact,
    useContacts,
    useCreateContact,
    useDeleteContact,
    useUpdateContact,
} from 'src/services/api';
import { Transition } from 'src/components/ModalTransition';
import {
    BlackButtonWhiteText,
    ModalSuccessButton,
    RedButtonWhiteText
} from 'src/common/Buttons';

import styles from 'src/components/edit-contacts-modal/EditContactsModal.module.css';
import modalButtonStyles from 'src/common/ModalButton.module.css';

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

export const contactDisplayName = (contact: Pick<IContact, 'name' | 'email'>): string => {
    return contact.name || contact.email;
};

interface IContactFieldsState {
    name: string;
    email: string;
    phoneNumber: string;
}

interface IContactFieldsProps {
    fields: IContactFieldsState;
    setFields(fields: IContactFieldsState): void;
}

function ContactFields({ fields, setFields }: IContactFieldsProps) {
    return (
        <div className={styles.contactFields}>
            <TextField
                autoFocus
                required
                placeholder="Email"
                type="email"
                value={fields.email}
                onChange={(event) => setFields({ ...fields, email: event.target.value })}
                fullWidth
                variant="standard"
                margin="dense"
                data-testid="contact-email-field"
            />
            <TextField
                placeholder="Name (optional)"
                value={fields.name}
                onChange={(event) => setFields({ ...fields, name: event.target.value })}
                fullWidth
                variant="standard"
                margin="dense"
                // reflects backend limit
                inputProps={{ maxLength: 60 }}
                data-testid="contact-name-field"
            />
            <TextField
                placeholder="Phone Number (optional)"
                type="tel"
                value={fields.phoneNumber}
                onChange={(event) => setFields({ ...fields, phoneNumber: event.target.value })}
                fullWidth
                variant="standard"
                margin="dense"
                data-testid="contact-phone-field"
            />
        </div>
    );
}

const EMPTY_FIELDS: IContactFieldsState = {
    name: '',
    email: '',
    phoneNumber: '',
};

interface IAddContactFormProps {
    onSave(fields: IContactFieldsState): Promise<boolean>;
}

function AddContactForm({ onSave }: IAddContactFormProps) {
    const [fields, setFields] = useState<IContactFieldsState>(EMPTY_FIELDS);

    const handleSave = async (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        const isSaved = await onSave(fields);
        if (isSaved) {
            setFields(EMPTY_FIELDS);
        }
    };

    return <form onSubmit={handleSave}>
        <ContactFields
            fields={fields}
            setFields={setFields}
        />
        <Button
            variant="contained"
            data-testid="new-contact-add-button"
            disabled={!EMAIL_PATTERN.test(fields.email.trim())}
            className={`${modalButtonStyles.modalButton} ${styles.addContactButton}`}
            onClick={() => handleSave()}
        >Add</Button>
    </form>;
}

interface IContactRowProps {
    contact: IContact;
    onEdit(): void;
    onDelete(): void;
}

function ContactRow({ contact, onEdit, onDelete }: IContactRowProps) {
    const details = [contact.name ? contact.email : null, contact.phone_number]
        .filter((detail): detail is string => !!detail)
        .join(' · ');

    return <div className={styles.contactRow}>
        <div className={styles.contactMain}>
            <ProfileImageRounded userName={contactDisplayName(contact)} width={32} height={32} />
            <div className={styles.contactInfo}>
                <Typography variant="body2">{contactDisplayName(contact)}</Typography>
                {details !== '' && (
                    <Typography variant="caption" color="text.secondary">{details}</Typography>
                )}
            </div>
        </div>
        <div className={styles.contactActions}>
            <IconButton onClick={onEdit} data-testid="edit-contact-button">
                <EditIcon />
            </IconButton>
            <IconButton onClick={onDelete} data-testid="delete-contact-button">
                <DeleteIcon />
            </IconButton>
        </div>
    </div>;
}

interface IEditContactFormProps {
    contact: IContact;
    onSave(contact: IContact, fields: IContactFieldsState): Promise<boolean>;
    onClose(): void;
}

function EditContactForm({ contact, onSave, onClose }: IEditContactFormProps) {
    const [fields, setFields] = useState<IContactFieldsState>({
        name: contact.name || '',
        email: contact.email,
        phoneNumber: contact.phone_number || '',
    });

    const handleSave = async (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        const isSaved = await onSave(contact, fields);
        if (isSaved) {
            onClose();
        }
    };

    return <form onSubmit={handleSave} className={styles.editForm}>
        <ContactFields
            fields={fields}
            setFields={setFields}
        />
        <div className={styles.modalButtonRow}>
            <BlackButtonWhiteText onClick={onClose} data-testid="edit-contact-back-button">
                Back
            </BlackButtonWhiteText>
            <ModalSuccessButton
                onClick={() => handleSave()}
                disabled={!EMAIL_PATTERN.test(fields.email.trim())}
                data-testid="edit-contact-save-button"
            >
                Save
            </ModalSuccessButton>
        </div>
    </form>;
}

interface IConfirmDeleteFormProps {
    contact: IContact;
    onCancel(): void;
    onConfirm(): Promise<void>;
}

function ConfirmDeleteForm({ contact, onCancel, onConfirm }: IConfirmDeleteFormProps) {
    return <form className={styles.confirmDeleteForm}>
        <DialogContentText>
            Are you sure you want to delete contact &quot;{contactDisplayName(contact)}&quot; for your entire team?
        </DialogContentText>
        <div className={`${styles.modalButtonRow} mt-2`}>
            <BlackButtonWhiteText onClick={onCancel} data-testid="delete-contact-cancel-button">Back</BlackButtonWhiteText>
            <RedButtonWhiteText onClick={onConfirm} data-testid="delete-contact-confirm-button">Delete</RedButtonWhiteText>
        </div>
    </form>;
}

interface Props {
    onClose: () => void;
}

export default function EditContactsModal({ onClose }: Props) {
    const { addNotification } = useNotifications();

    const { data: contacts } = useContacts();
    const { mutateAsync: createContact } = useCreateContact();
    const { mutateAsync: updateContact } = useUpdateContact();
    const { mutateAsync: deleteContact } = useDeleteContact();

    const [selectedContactToEdit, setSelectedContactToEdit] = useState<IContact>();
    const [selectedContactToDelete, setSelectedContactToDelete] = useState<IContact>();

    const handleCreate = async (fields: IContactFieldsState): Promise<boolean> => {
        try {
            await createContact({
                email: fields.email.trim(),
                name: fields.name.trim() || null,
                phone_number: fields.phoneNumber.trim() || null,
            });
            addNotification('Contact created', 'success');
            return true;
        } catch (error) {
            if (isStandardErrorResponse(error)) {
                addNotification(error.msg || 'Failed to create contact', 'error');
            }
            return false;
        }
    };

    const handleUpdate = async (contact: IContact, fields: IContactFieldsState): Promise<boolean> => {
        try {
            await updateContact({
                id: contact.id,
                email: fields.email.trim(),
                name: fields.name.trim() || null,
                phone_number: fields.phoneNumber.trim() || null,
            });
            addNotification('Contact updated', 'success');
            return true;
        } catch (error) {
            if (isStandardErrorResponse(error)) {
                addNotification(error.msg || 'Failed to update contact', 'error');
            }
            return false;
        }
    };

    const handleDelete = async (contactId: number) => {
        try {
            await deleteContact(contactId);
            addNotification('Contact deleted', 'success');
            setSelectedContactToDelete(undefined);
        } catch (error) {
            if (isStandardErrorResponse(error)) {
                addNotification(error.msg || 'Failed to delete contact', 'error');
            }
        }
    };

    let innerContent: React.ReactNode;
    if (selectedContactToEdit) {
        innerContent = (
            <EditContactForm
                contact={selectedContactToEdit}
                onSave={handleUpdate}
                onClose={() => setSelectedContactToEdit(undefined)}
            />
        );
    } else if (selectedContactToDelete) {
        innerContent = (
            <ConfirmDeleteForm
                contact={selectedContactToDelete}
                onCancel={() => setSelectedContactToDelete(undefined)}
                onConfirm={() => handleDelete(selectedContactToDelete.id)}
            />
        );
    } else {
        innerContent = (
            <>
                <AddContactForm onSave={handleCreate} />

                <div>
                    <Typography variant="h6" className={styles.existingContactsHeading}>Existing Contacts</Typography>
                    {contacts?.length === 0 && <Alert severity="info">Your team does not have any contacts yet.</Alert>}
                    {!!contacts?.length && (
                        <div>
                            {contacts.map((contact) => (
                                <ContactRow
                                    key={contact.id}
                                    contact={contact}
                                    onEdit={() => setSelectedContactToEdit(contact)}
                                    onDelete={() => setSelectedContactToDelete(contact)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </>
        );
    }

    return (
        <Dialog open={true} onClose={onClose} TransitionComponent={Transition} fullWidth>
            <DialogTitle className={styles.dialogTitle}>
                Manage Contacts
                <HelpIcon title="Contacts are people your team loops in on tours, like onsite leasing agents. Turn a contact on in a property's tour settings to include it in tour invitation emails. It will be visible to all of your team members." />
            </DialogTitle>

            <DialogContent>
                {innerContent}
            </DialogContent>
        </Dialog>
    );
}
